import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { FormsModule } from '@angular/forms';
import { CountriesService, CountryDto } from '../../../shared/services/countries.service';
import { UniversitiesService, UniversityDto, UniversityPayload } from '../../../shared/services/universities.service';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminTableComponent,
  StatusBadgeComponent,
} from '../../../shared/admin';

@Component({
  selector: 'app-universities-page',
  standalone: true,
  imports: [
    FormsModule,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminModalComponent,
    StatusBadgeComponent,
  ],
  template: `
    <section>
      <app-admin-page-header>
        <ng-container actions>
          <label class="cg-admin-field" style="flex-direction: row; align-items: center; gap: var(--cg-space-2)">
            <span class="cg-admin-field__label">Country</span>
            <select
              class="cg-select"
              [(ngModel)]="filterCountryId"
              (ngModelChange)="refresh()"
              name="filterCountry"
            >
              <option value="">All</option>
              @for (c of countries(); track c._id) {
                <option [value]="c._id">{{ c.flagIcon }} {{ c.name }}</option>
              }
            </select>
          </label>
          @if (showCreate()) {
            <app-admin-button variant="primary" type="button" (click)="openCreate()" [disabled]="!canCreate()">New university</app-admin-button>
          }
        </ng-container>
      </app-admin-page-header>

      <app-admin-table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Country</th>
            <th>Order</th>
            <th>Status</th>
            <th style="text-align: right">Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (list().length === 0) {
            <tr>
              <td colspan="6">
                <app-admin-empty-state icon="graduation-cap" title="No universities yet" />
              </td>
            </tr>
          }
          @for (u of list(); track u._id) {
            <tr>
              <td><strong>{{ u.name }}</strong></td>
              <td><code class="cg-input--mono">{{ u.slug }}</code></td>
              <td>{{ countryName(u.countryId) }}</td>
              <td>{{ u.order ?? 0 }}</td>
              <td>
                <app-status-badge [variant]="u.status === 'PUBLISHED' ? 'published' : 'draft'">{{ u.status || 'DRAFT' }}</app-status-badge>
              </td>
              <td>
                <div class="cg-admin-table-actions">
                  @if (showEdit()) {
                    <app-admin-button variant="ghost" type="button" (click)="edit(u)" [disabled]="!canEdit()">Edit</app-admin-button>
                  }
                  @if (showPublish()) {
                    <app-admin-button variant="secondary" type="button" (click)="togglePublish(u)" [disabled]="!canPublish()">
                      {{ u.status === 'PUBLISHED' ? 'Unpublish' : 'Publish' }}
                    </app-admin-button>
                  }
                  @if (showDelete()) {
                    <app-admin-button variant="danger" type="button" (click)="remove(u)" [disabled]="!canDelete()">Delete</app-admin-button>
                  }
                </div>
              </td>
            </tr>
          }
        </tbody>
      </app-admin-table>

      @if (showForm()) {
        <app-admin-modal [title]="editingId() ? 'Edit university' : 'New university'" size="lg" (closed)="closeForm()">
            <div class="cg-admin-form-grid cg-admin-form-grid--2">
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">Country</span>
                <select class="mt-1 w-full rounded border p-2" [(ngModel)]="form.countryId" name="countryId">
                  @for (c of countries(); track c._id) {
                    <option [value]="c._id">{{ c.flagIcon }} {{ c.name }}</option>
                  }
                </select>
              </label>
              <label class="block text-sm">
                <span class="text-slate-700">Name</span>
                <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.name" name="name" />
              </label>
              <label class="block text-sm">
                <span class="text-slate-700">Slug</span>
                <input class="mt-1 w-full rounded border p-2 font-mono" [(ngModel)]="form.slug" name="slug" />
              </label>
              <label class="block text-sm">
                <span class="text-slate-700">Order</span>
                <input type="number" class="mt-1 w-full rounded border p-2" [(ngModel)]="form.order" name="order" />
              </label>
              <label class="block text-sm">
                <span class="text-slate-700">Status</span>
                <select class="mt-1 w-full rounded border p-2" [(ngModel)]="form.status" name="status">
                  <option value="DRAFT">DRAFT</option>
                  <option value="PUBLISHED">PUBLISHED</option>
                </select>
              </label>
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">Location</span>
                <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.location" name="location" />
              </label>
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">Ranking label</span>
                <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.rankingLabel" name="rankingLabel" />
              </label>
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">Short description</span>
                <textarea class="mt-1 w-full rounded border p-2" rows="2" [(ngModel)]="form.shortDescription" name="shortDescription"></textarea>
              </label>
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">Tuition summary</span>
                <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.tuitionSummary" name="tuitionSummary" />
              </label>
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">Card image media asset id</span>
                <input class="mt-1 w-full rounded border p-2 font-mono text-xs" [(ngModel)]="form.cardImageMediaAssetId" name="cardImageMediaAssetId" />
              </label>
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">CTA URL</span>
                <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.ctaUrl" name="ctaUrl" />
              </label>
            </div>
            @if (error(); as msg) {
              <p class="mt-3 text-sm text-red-600">{{ msg }}</p>
            }
            <div class="cg-admin-page-header__actions" style="margin-top: var(--cg-space-5); justify-content: flex-end">
              <app-admin-button variant="cancel" type="button" (click)="closeForm()">Cancel</app-admin-button>
              @if (showSaveModal()) {
                <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="saving()">
                  {{ saving() ? 'Saving...' : 'Save' }}
                </app-admin-button>
              }
            </div>
        </app-admin-modal>
      }
    </section>
  `,
})
export class UniversitiesPageComponent {
  private readonly uniApi = inject(UniversitiesService);
  private readonly countriesService = inject(CountriesService);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.universities', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.universities', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.universities', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.universities', 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow('website.universities', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.universities', 'delete'));
  readonly showPublish = computed(() => this.permissions.canShow('website.universities', 'publish'));
  readonly canPublish = computed(() => this.permissions.canExecute('website.universities', 'publish'));
  readonly canSaveModal = computed(() =>
    this.editingId() ? this.canEdit() : this.canCreate(),
  );
  readonly showSaveModal = computed(() =>
    this.editingId() ? this.showEdit() : this.showCreate(),
  );

  readonly countries = signal<CountryDto[]>([]);
  readonly list = signal<UniversityDto[]>([]);
  filterCountryId = '';

  readonly showForm = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  form: UniversityPayload & { order: number } = this.emptyForm();

  constructor() {
    this.countriesService.listPlatformCountries().subscribe((c) => this.countries.set(c));
    this.refresh();
  }

  countryName(countryId: string): string {
    const id = String(countryId);
    const c = this.countries().find((x) => x._id === id);
    return c ? `${c.flagIcon ?? ''} ${c.name}`.trim() : id;
  }

  private emptyForm(): UniversityPayload & { order: number } {
    return {
      name: '',
      slug: '',
      countryId: '',
      location: '',
      rankingLabel: '',
      shortDescription: '',
      tuitionSummary: '',
      cardImageMediaAssetId: '',
      ctaUrl: '',
      order: 0,
      status: 'DRAFT',
    };
  }

  refresh(): void {
    this.uniApi.list(this.filterCountryId || undefined).subscribe((items) => this.list.set(items));
  }

  openCreate(): void {
    const first = this.countries()[0];
    this.form = { ...this.emptyForm(), countryId: first?._id ?? '' };
    this.editingId.set(null);
    this.error.set(null);
    this.showForm.set(true);
  }

  edit(u: UniversityDto): void {
    this.form = {
      name: u.name,
      slug: u.slug,
      countryId: String(u.countryId),
      location: u.location ?? '',
      rankingLabel: u.rankingLabel ?? '',
      shortDescription: u.shortDescription ?? '',
      tuitionSummary: u.tuitionSummary ?? '',
      cardImageMediaAssetId: u.cardImageMediaAssetId ?? '',
      ctaUrl: u.ctaUrl ?? '',
      order: u.order ?? 0,
      status: u.status ?? 'DRAFT',
    };
    this.editingId.set(u._id);
    this.error.set(null);
    this.showForm.set(true);
  }

  togglePublish(u: UniversityDto): void {
    const next = u.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    this.uniApi.update(u._id, { status: next }).subscribe(() => this.refresh());
  }

  remove(u: UniversityDto): void {
    if (!confirm(`Delete ${u.name}?`)) return;
    this.uniApi.remove(u._id).subscribe(() => this.refresh());
  }

  closeForm(): void {
    this.showForm.set(false);
  }

  save(): void {
    const payload: UniversityPayload = {
      name: this.form.name,
      slug: this.form.slug,
      countryId: this.form.countryId,
      location: this.form.location || undefined,
      rankingLabel: this.form.rankingLabel || undefined,
      shortDescription: this.form.shortDescription || undefined,
      tuitionSummary: this.form.tuitionSummary || undefined,
      cardImageMediaAssetId: this.form.cardImageMediaAssetId || undefined,
      ctaUrl: this.form.ctaUrl || undefined,
      order: Number(this.form.order) || 0,
      status: this.form.status,
    };

    this.saving.set(true);
    this.error.set(null);
    const id = this.editingId();
    const req$ = id ? this.uniApi.update(id, payload) : this.uniApi.create(payload);
    req$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.refresh();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message ?? 'Save failed.');
      },
    });
  }
}
